// Library / Home recording card: the DS RecordingCard look (design-system/components/media/
// RecordingCard.jsx — 12px card, hairline, xs → md shadow and a 2px lift on hover, 16:9
// thumbnail over the ink gradient, a centred uploading / transcoding overlay, the hover play
// disc, a glass duration pill, then title + more-actions, "Owner · 2 hours ago" and one badge
// row). It keeps what the ported `RecordingCard` has no props for: the 20px owner avatar, the
// five visibility labels (FR-S*), the legal-hold badge, the Notes link for meeting recordings,
// the stale-thumbnail probe (FR-ST18), rename, move-to-folder and delete (FR-L2).
import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Avatar, Badge, Callout, Icon, IconButton, Menu, MenuItem, Spinner } from "@grackle/ui";
import ConfirmDialog from "../components/ConfirmDialog";
import { api, deleteErrorMessage } from "../api/client";
import type { GrackleOut } from "../api/types";
import { useSession } from "../session";
import { formatDuration, formatRelativeTime, visibilityIcon, visibilityLabel } from "./format";
import MoveToFolderDialog from "./MoveToFolderDialog";
import RenameGrackleDialog from "./RenameGrackleDialog";
import { notesPath } from "./routes";

/** Exported for `GrackleCard.test.ts` (W7: "GrackleCard shows the badge only when on_legal_hold
 * true") — `on_legal_hold` is `boolean | undefined` (omitted for anyone but the owner/admin, per
 * S1), so this pins the badge to the strict `true` case rather than any truthy value. */
export function shouldShowHoldBadge(grackle: Pick<GrackleOut, "on_legal_hold">): boolean {
  return grackle.on_legal_hold === true;
}

/** The one status / visibility badge. Status is never colour alone: each carries an icon and a
 * word. A "ready" recording shows its visibility instead. */
function StateBadge({ grackle }: { grackle: GrackleOut }) {
  if (grackle.status === "uploading") {
    return (
      <Badge size="sm" tone="processing" icon="cloud-arrow-up">
        Uploading
      </Badge>
    );
  }
  // SD25/SD57: `processing` is the publish job (audio track, poster, and a remux only if an
  // operator turned muxing on). Nothing is necessarily transcoded, so the word doesn't say so.
  if (grackle.status === "processing") {
    return (
      <Badge size="sm" tone="processing" icon="hourglass-high">
        Processing
      </Badge>
    );
  }
  if (grackle.status === "failed") {
    return (
      <Badge size="sm" tone="outline" icon="warning-circle" className="lib-badge--failed">
        Processing failed
      </Badge>
    );
  }
  return (
    <Badge
      size="sm"
      tone={grackle.visibility === "private" ? "private" : "shared"}
      icon={visibilityIcon(grackle.visibility)}
    >
      {visibilityLabel(grackle.visibility)}
    </Badge>
  );
}

export default function GrackleCard({
  grackle,
  onThumbnailStale,
  onDeleted,
  onMoved,
}: {
  grackle: GrackleOut;
  /** thumbnail_url is a signed, windowed URL (FR-ST18) — it can expire while the card is still
   * on screen. A load failure means the window most likely lapsed, so the fix is to refetch the
   * item for a fresh URL, not to show a broken image. */
  onThumbnailStale: (grackleId: string) => void;
  /** Called after a successful delete (F2: moved to trash) so the caller can drop this card
   * from its list. */
  onDeleted: (grackleId: string) => void;
  /** Called after a successful "Move to…" (F2 / FR-L1). Optional: the library and home grids
   * aren't folder-filtered, so moving a card doesn't change what they show. */
  onMoved?: (grackleId: string, folderId: string | null) => void;
}) {
  const { user } = useSession();
  // Reset PR3: a meeting recording opens its notes too. Home and Library both live under
  // /o/:slug, so the card can build the org-scoped notes route itself.
  const { slug } = useParams();
  const notesTo = grackle.is_meeting && slug ? notesPath(slug, grackle.id) : null;
  const poster = grackle.status === "ready" && grackle.thumbnail_url ? grackle.thumbnail_url : undefined;
  const processing = grackle.status === "uploading" || grackle.status === "processing";
  const ready = grackle.status === "ready";

  const [menuOpen, setMenuOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [moveOpen, setMoveOpen] = useState(false);
  const [renameOpen, setRenameOpen] = useState(false);
  // A rename shows here at once; the callers' lists aren't told, and their next fetch agrees.
  const [renamedTitle, setRenamedTitle] = useState<string | null>(null);
  const title = renamedTitle ?? grackle.title;
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menuOpen) return;
    function onDocClick(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setMenuOpen(false);
    }
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [menuOpen]);

  function onDelete() {
    setMenuOpen(false);
    setDeleteError(null);
    setConfirmOpen(true);
  }

  async function onDeleteConfirm() {
    setConfirmOpen(false);
    setDeleting(true);
    try {
      await api("DELETE", `/grackles/${grackle.id}`);
      onDeleted(grackle.id);
    } catch (err) {
      // Reopen the dialog with the failure inline so the user knows the recording is
      // still there — a silently-swallowed delete failure looks like a successful delete.
      setDeleteError(deleteErrorMessage(err, "Couldn't delete this recording."));
      setDeleting(false);
      setConfirmOpen(true);
    }
  }

  // A member only manages their own recordings from the grid — an admin removing someone
  // else's content has the dedicated, audit-logged admin Content page for that (FR-AD2), not a
  // quiet card menu.
  const canDelete = user?.id === grackle.owner.id;

  const cls = ["lib-card", processing && "lib-card--processing", ready && "lib-card--ready", menuOpen && "is-menu-open"]
    .filter(Boolean)
    .join(" ");

  return (
    <article className={cls}>
      <div className="lib-card__thumb">
        {/* DS: no product photography; a missing poster falls back to the ink gradient. */}
        {poster ? (
          <img src={poster} alt="" loading="lazy" className="lib-card__img" onError={() => onThumbnailStale(grackle.id)} />
        ) : (
          <div className="lib-card__fallback" />
        )}
        {processing ? (
          <div className="lib-card__overlay">
            <Spinner size={20} label="" />
            <span>{grackle.status === "uploading" ? "Uploading" : "Processing"}</span>
          </div>
        ) : ready ? (
          <div className="lib-card__play" aria-hidden="true">
            <span className="lib-card__playdisc">
              <Icon name="play" fill size={20} />
            </span>
          </div>
        ) : null}
        {ready && grackle.duration_ms != null ? (
          <Badge tone="glass" size="sm" className="lib-card__duration gk-num">
            {formatDuration(grackle.duration_ms)}
          </Badge>
        ) : null}
      </div>

      <div className="lib-card__body">
        <div className="lib-card__head">
          <h3 className="lib-card__title">
            {/* The title is the link; its ::after covers the whole card (library.css). */}
            <Link to={`/g/${grackle.id}`} className="lib-card__link">
              {title}
            </Link>
          </h3>
          {canDelete ? (
            <div className="lib-card__menu" ref={menuRef}>
              <IconButton
                icon="dots-three"
                label="Recording actions"
                size="sm"
                aria-haspopup="menu"
                aria-expanded={menuOpen}
                onClick={() => setMenuOpen((v) => !v)}
              />
              {menuOpen ? (
                <Menu>
                  <MenuItem
                    icon="pencil-simple"
                    onClick={() => {
                      setMenuOpen(false);
                      setRenameOpen(true);
                    }}
                  >
                    Rename
                  </MenuItem>
                  <MenuItem
                    icon="folder-simple"
                    onClick={() => {
                      setMenuOpen(false);
                      setMoveOpen(true);
                    }}
                  >
                    Move to…
                  </MenuItem>
                  <MenuItem icon="trash" tone="danger" onClick={onDelete} disabled={deleting}>
                    {deleting ? "Moving…" : "Delete"}
                  </MenuItem>
                </Menu>
              ) : null}
            </div>
          ) : null}
        </div>

        <div className="lib-card__meta">
          <Avatar name={grackle.owner.name} size={20} speaker={1} />
          <span className="lib-card__meta-text">
            {grackle.owner.name} · {formatRelativeTime(grackle.created_at)}
          </span>
        </div>

        <div className="lib-card__badges">
          <StateBadge grackle={grackle} />
          {shouldShowHoldBadge(grackle) ? (
            <Badge size="sm" tone="outline" icon="flag">
              On legal hold
            </Badge>
          ) : null}
          {notesTo ? (
            <Link to={notesTo} className="lib-card__notes" aria-label={`Notes for ${title}`}>
              <Icon name="file-text" size={14} />
              Notes
            </Link>
          ) : null}
        </div>
      </div>

      {canDelete ? (
        <>
          <ConfirmDialog
            open={confirmOpen}
            title="Move to trash"
            message={
              <>
                {`Move "${title}" to trash? It's deleted forever after 30 days.`}
                {deleteError ? <Callout tone="danger" icon="warning-circle">{deleteError}</Callout> : null}
              </>
            }
            confirmLabel="Move to trash"
            onClose={() => {
              setConfirmOpen(false);
              setDeleting(false);
            }}
            onConfirm={onDeleteConfirm}
          />
          {renameOpen ? (
            <RenameGrackleDialog
              grackleId={grackle.id}
              title={title}
              onClose={() => setRenameOpen(false)}
              onRenamed={setRenamedTitle}
            />
          ) : null}
          {moveOpen ? (
            <MoveToFolderDialog
              orgId={grackle.org_id}
              grackleId={grackle.id}
              onClose={() => setMoveOpen(false)}
              onMoved={(folderId) => onMoved?.(grackle.id, folderId)}
            />
          ) : null}
        </>
      ) : null}
    </article>
  );
}
